import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import {
  needsReview,
  serializeChordPro,
  serializeChordSheet,
  type SongDocument,
} from '../../domain'
import { downloadText } from '../../platform/download'
import { getDb, type EditorDraft } from '../../storage/db'
import {
  clearDraft,
  deleteSong,
  getDraft,
  getSongDocument,
  listRevisions,
  saveDraft,
  saveRevision,
  setMainRevision,
  updateSong,
} from '../../storage/repository'
import common from '../common.module.css'
import {
  buildContent,
  fileSlug,
  formFromDocument,
  fromDraft,
  isDirty,
  keyOptions,
  timeOptions,
  toDraft,
  type FormState,
} from '../edit/form'
import { useLive } from '../hooks/useLive'
import { Sheet } from '../play/Sheet'
import { usePreferences } from '../prefs'
import { navigate, paths } from '../router'
import { t } from '../strings'
import styles from './Editor.module.css'

const AUTOSAVE_MS = 500
const MIN_BPM = 20
const MAX_BPM = 320
const DEFAULT_BPM = 100

export function Editor({ arrangementId }: { arrangementId: string }) {
  const { data: doc, loading } = useLive(
    () => getSongDocument(getDb(), arrangementId),
    [arrangementId],
  )
  // El borrador se lee una sola vez: después lo escribe el propio editor.
  const [draft, setDraft] = useState<{ ready: boolean; value?: EditorDraft }>({ ready: false })
  useEffect(() => {
    let cancelled = false
    getDraft(getDb(), arrangementId).then((value) => {
      if (!cancelled) setDraft(value ? { ready: true, value } : { ready: true })
    })
    return () => {
      cancelled = true
    }
  }, [arrangementId])

  if (loading || !draft.ready) return <p className={common.muted}>{t.editor.loading}</p>
  if (!doc) {
    return (
      <div className={common.page}>
        <p className={common.noticeError}>{t.editor.notFound}</p>
        <a href={`#${paths.repertoire}`}>{t.nav.repertoire}</a>
      </div>
    )
  }
  return <EditorView key={arrangementId} doc={doc} draft={draft.value} />
}

interface Status {
  ok: boolean
  text: string
}

const clampBpm = (bpm: number) => Math.min(MAX_BPM, Math.max(MIN_BPM, bpm))

function EditorView({ doc, draft }: { doc: SongDocument; draft: EditorDraft | undefined }) {
  const preferences = usePreferences()
  const e = t.editor
  const arrangementId = doc.arrangement.id
  const base = doc.revision.content

  const restored = draft ? fromDraft(draft) : null
  const [form, setForm] = useState<FormState>(() => restored ?? formFromDocument(doc))
  const [recovered, setRecovered] = useState(
    () => restored !== null && isDirty(doc, restored, buildContent(base, restored)),
  )
  const [tab, setTab] = useState<'edit' | 'preview'>('edit')
  const [status, setStatus] = useState<Status | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [exportNote, setExportNote] = useState<string | null>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const deferred = useDeferredValue(form)
  const built = useMemo(() => buildContent(base, deferred), [base, deferred])
  const dirty = isDirty(doc, deferred, built)
  const { data: revisions } = useLive(() => listRevisions(getDb(), arrangementId), [arrangementId])

  const patch = (changes: Partial<FormState>) => setForm((current) => ({ ...current, ...changes }))

  // Cada cambio se guarda al momento como borrador: iPadOS puede cerrar la app en cualquier
  // momento (§9.5). Se escribe tras un instante sin teclear y al ocultarse o salir de la pantalla.
  const latest = useRef({ form, dirty })
  useEffect(() => {
    latest.current = { form, dirty }
  })
  useEffect(() => {
    const timer = setTimeout(() => {
      const db = getDb()
      if (dirty) {
        void saveDraft(db, toDraft(arrangementId, doc.revision.id, form, new Date().toISOString()))
      } else {
        void clearDraft(db, arrangementId)
      }
    }, AUTOSAVE_MS)
    return () => clearTimeout(timer)
  }, [form, dirty, arrangementId, doc.revision.id])
  useEffect(() => {
    const flush = () => {
      if (!latest.current.dirty) return
      const now = new Date().toISOString()
      void saveDraft(getDb(), toDraft(arrangementId, doc.revision.id, latest.current.form, now))
    }
    const onVisibility = () => {
      if (window.document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    window.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', flush)
      window.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [arrangementId, doc.revision.id])

  const save = useCallback(async () => {
    setSaving(true)
    setStatus(null)
    try {
      const db = getDb()
      const now = new Date().toISOString()
      const title = form.title.trim()
      const artist = form.artist.trim()
      if (title === '') throw new Error(t.newSong.needTitle)
      const { content } = buildContent(base, form)
      const renamed = title !== doc.song.title || artist !== doc.song.artist
      if (renamed) await updateSong(db, doc.song.id, { title, artist }, now)
      const saved = await saveRevision(db, arrangementId, content, {
        now,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
      await clearDraft(db, arrangementId)
      setReason('')
      setRecovered(false)
      setForm((current) => ({ ...current, title, artist, reviewed: false }))
      setStatus({ ok: true, text: saved.created || renamed ? e.saved : e.savedNothing })
    } catch (cause) {
      setStatus({
        ok: false,
        text: e.saveError(cause instanceof Error ? cause.message : String(cause)),
      })
    } finally {
      setSaving(false)
    }
  }, [base, form, doc.song, arrangementId, reason, e])

  async function makeMain(revisionId: string) {
    try {
      const db = getDb()
      await setMainRevision(db, arrangementId, revisionId, new Date().toISOString())
      await clearDraft(db, arrangementId)
      const fresh = await getSongDocument(db, arrangementId)
      if (fresh) setForm(formFromDocument(fresh))
      setRecovered(false)
    } catch (cause) {
      setStatus({
        ok: false,
        text: e.saveError(cause instanceof Error ? cause.message : String(cause)),
      })
    }
  }

  async function remove() {
    await deleteSong(getDb(), doc.song.id, new Date().toISOString())
    navigate(paths.repertoire)
  }

  const discardDraft = () => {
    setForm(formFromDocument(doc))
    setRecovered(false)
  }

  /** Inserta texto donde está el cursor y deja el cursor `cursor` caracteres dentro. */
  function insert(text: string, cursor = text.length) {
    const area = bodyRef.current
    const start = area?.selectionStart ?? form.body.length
    const end = area?.selectionEnd ?? start
    const before = form.body.slice(0, start)
    const lead =
      text.startsWith('\n') && (before === '' || before.endsWith('\n')) ? text.slice(1) : text
    const offset = lead === text ? cursor : cursor - 1
    patch({ body: before + lead + form.body.slice(end) })
    requestAnimationFrame(() => {
      area?.focus()
      const position = start + offset
      area?.setSelectionRange(position, position)
    })
  }

  function insertSection(kind: string) {
    const open = `\n{start_of_${kind}}\n`
    insert(`${open}\n{end_of_${kind}}\n`, open.length)
  }

  const exportChordPro = () => {
    const { text, losses } = serializeChordPro({
      title: doc.song.title,
      artist: doc.song.artist,
      content: doc.revision.content,
    })
    downloadText(`${fileSlug(doc.song.title)}.cho`, text)
    setExportNote(losses.length > 0 ? e.exportLosses(losses.join('; ')) : null)
  }

  const exportSheet = () => {
    const { text, losses } = serializeChordSheet(doc.revision.content, {
      notation: preferences.notation,
    })
    downloadText(
      `${fileSlug(doc.song.title)}.txt`,
      `${doc.song.title}\n${doc.song.artist}\n\n${text}`,
    )
    setExportNote(losses.length > 0 ? e.exportLosses(losses.join('; ')) : null)
  }

  const provenance = (path: string) => {
    const entry = built.content.provenance[path]
    return entry ? (
      <p className={styles.provenance}>{e.provenance(entry.kind, !!entry.needsReview)}</p>
    ) : null
  }

  const origin = built.content.provenance.content
  const sourceLink = doc.song.links?.[0]
  const keys = keyOptions(base.notation, form.shapeKey)
  const times = timeOptions(form.timeSignature)
  const issues = built.issues.filter(
    (issue) => issue.severity === 'warning' || issue.severity === 'error',
  )
  const showReview = needsReview(base) && !form.reviewed

  return (
    <div className={`${common.page} ${styles.editor}`}>
      <div className={styles.top}>
        <h1>{e.title}</h1>
        <a className={common.spacer} href={`#${paths.play(arrangementId)}`}>
          <button>{e.back}</button>
        </a>
      </div>

      {recovered && (
        <div className={common.noticeWarn} role="status">
          <span>{e.draftRecovered} </span>
          <button onClick={discardDraft}>{e.draftDiscard}</button>
        </div>
      )}
      {(showReview || origin) && (
        <div className={showReview ? common.noticeWarn : common.notice}>
          {origin && (
            <p style={{ margin: '0 0 6px' }}>
              {e.provenance(origin.kind, !!origin.needsReview && !form.reviewed)}
              {sourceLink && (
                <>
                  {' · '}
                  <a href={sourceLink.url} target="_blank" rel="noopener noreferrer">
                    {sourceLink.label ?? sourceLink.url}
                  </a>
                </>
              )}
            </p>
          )}
          {showReview && (
            <div className={common.row}>
              <span>{e.review}</span>
              <button onClick={() => patch({ reviewed: true })}>{e.reviewAccept}</button>
            </div>
          )}
        </div>
      )}

      <div className={styles.tabs} role="tablist">
        {(['edit', 'preview'] as const).map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? common.toggleOn : undefined}
            onClick={() => setTab(value)}
          >
            {e.tab[value]}
          </button>
        ))}
      </div>

      <div className={styles.columns}>
        <div className={`${styles.formColumn} ${tab === 'preview' ? styles.hiddenOnNarrow : ''}`}>
          <div className={styles.grid}>
            <label className={common.field}>
              <span>{e.songTitle}</span>
              <input
                value={form.title}
                onChange={(event) => patch({ title: event.target.value })}
              />
            </label>
            <label className={common.field}>
              <span>{e.songArtist}</span>
              <input
                value={form.artist}
                onChange={(event) => patch({ artist: event.target.value })}
              />
            </label>

            <label className={common.field}>
              <span>{e.shapeKey}</span>
              <select
                value={form.shapeKey}
                onChange={(event) => patch({ shapeKey: event.target.value })}
              >
                <option value="">{e.keyNone}</option>
                {keys.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
              {provenance('shapeKey')}
            </label>
            <label className={common.field}>
              <span>{e.capo}</span>
              <select
                value={form.capo}
                onChange={(event) => patch({ capo: Number(event.target.value) })}
              >
                {Array.from({ length: 13 }, (_, fret) => (
                  <option key={fret} value={fret}>
                    {fret === 0 ? e.capoNone : fret}
                  </option>
                ))}
              </select>
              {provenance('capo')}
            </label>

            <div className={common.field}>
              <span>{e.tempo}</span>
              <div className={styles.stepper}>
                {form.tempoBpm === null ? (
                  <button onClick={() => patch({ tempoBpm: DEFAULT_BPM })}>
                    {e.tempoNone} · +
                  </button>
                ) : (
                  <>
                    <button
                      aria-label={`${e.tempoDown} 5`}
                      onClick={() =>
                        patch({ tempoBpm: clampBpm((form.tempoBpm ?? DEFAULT_BPM) - 5) })
                      }
                    >
                      −5
                    </button>
                    <button
                      aria-label={`${e.tempoDown} 1`}
                      onClick={() =>
                        patch({ tempoBpm: clampBpm((form.tempoBpm ?? DEFAULT_BPM) - 1) })
                      }
                    >
                      −1
                    </button>
                    <span className={styles.stepValue}>{t.play.bpm(form.tempoBpm)}</span>
                    <button
                      aria-label={`${e.tempoUp} 1`}
                      onClick={() =>
                        patch({ tempoBpm: clampBpm((form.tempoBpm ?? DEFAULT_BPM) + 1) })
                      }
                    >
                      +1
                    </button>
                    <button
                      aria-label={`${e.tempoUp} 5`}
                      onClick={() =>
                        patch({ tempoBpm: clampBpm((form.tempoBpm ?? DEFAULT_BPM) + 5) })
                      }
                    >
                      +5
                    </button>
                    <button onClick={() => patch({ tempoBpm: null })}>×</button>
                  </>
                )}
              </div>
              {provenance('tempo.reference')}
            </div>
            <label className={common.field}>
              <span>{e.time}</span>
              <select
                value={form.timeSignature}
                onChange={(event) => patch({ timeSignature: event.target.value })}
              >
                <option value="">{e.timeNone}</option>
                {times.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              {provenance('timeSignature')}
            </label>
          </div>

          <label className={common.field} htmlFor="editor-body">
            <span>{e.body}</span>
          </label>
          <p className={`${common.muted} ${common.small}`} style={{ margin: '0 0 4px' }}>
            {e.bodyHint}
          </p>
          <div className={styles.toolbar} role="group" aria-label={e.insert}>
            <button onClick={() => insert('[]', 1)}>{e.insertChord}</button>
            <button onClick={() => insertSection('intro')}>{e.insertIntro}</button>
            <button onClick={() => insertSection('verse')}>{e.insertVerse}</button>
            <button onClick={() => insertSection('chorus')}>{e.insertChorus}</button>
            <button onClick={() => insertSection('bridge')}>{e.insertBridge}</button>
            <button onClick={() => insert('\n{repeat: }\n', 10)}>{e.insertRepeat}</button>
          </div>
          <textarea
            id="editor-body"
            ref={bodyRef}
            className={styles.body}
            wrap="off"
            value={form.body}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => patch({ body: event.target.value })}
          />
          {issues.length > 0 && (
            <ul className={styles.issues} aria-label={e.issues}>
              {issues.slice(0, 8).map((issue, index) => (
                <li key={index}>{issue.message}</li>
              ))}
              {issues.length > 8 && <li>… y {issues.length - 8} más</li>}
            </ul>
          )}

          <label className={common.field}>
            <span>{e.notes}</span>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(event) => patch({ notes: event.target.value })}
            />
          </label>
        </div>

        <div
          className={`${styles.previewColumn} ${tab === 'edit' ? styles.hiddenOnNarrow : ''}`}
          aria-label={e.preview}
        >
          <div className={styles.preview}>
            <Sheet content={built.content} notation={preferences.notation} />
          </div>
        </div>
      </div>

      <div className={styles.saveBar}>
        <span className={dirty ? styles.dirty : common.muted}>
          {dirty ? e.unsaved : e.allSaved}
        </span>
        <input
          aria-label={e.reason}
          placeholder={e.reason}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <button className="primary" disabled={saving || !dirty} onClick={() => void save()}>
          {saving ? e.saving : e.save}
        </button>
        {status && (
          <span role="status" style={{ color: status.ok ? 'var(--ok)' : 'var(--error)' }}>
            {status.text}
          </span>
        )}
      </div>

      <section className={common.card}>
        <h2>{e.history}</h2>
        <p className={common.muted}>{e.historyHint}</p>
        <ul className={styles.history}>
          {(revisions ?? []).map((revision) => {
            const isMain = revision.id === doc.arrangement.mainRevisionId
            return (
              <li key={revision.id} className={isMain ? styles.revisionMain : styles.revision}>
                <div className={styles.revisionInfo}>
                  <p>
                    <strong>{new Date(revision.createdAt).toLocaleString('es-ES')}</strong> ·{' '}
                    {e.author[revision.author]}
                  </p>
                  <p className={common.muted}>{revision.reason ?? e.noReason}</p>
                </div>
                {isMain ? (
                  <span className={common.badge}>{e.main}</span>
                ) : (
                  <button disabled={dirty} onClick={() => void makeMain(revision.id)}>
                    {e.makeMain}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      <section className={common.card}>
        <h2>{e.export}</h2>
        <div className={common.row}>
          <button onClick={exportChordPro}>{e.exportChordpro}</button>
          <button onClick={exportSheet}>{e.exportSheet}</button>
        </div>
        {exportNote && <p className={common.muted}>{exportNote}</p>}
      </section>

      <section className={`${common.card} ${styles.bottom}`}>
        {confirmDelete ? (
          <div className={styles.confirm}>
            <span>{e.deleteAsk}</span>
            <button className={common.danger} onClick={() => void remove()}>
              {e.deleteYes}
            </button>
            <button onClick={() => setConfirmDelete(false)}>{e.deleteNo}</button>
          </div>
        ) : (
          <button className={common.danger} onClick={() => setConfirmDelete(true)}>
            {e.delete}
          </button>
        )}
      </section>
    </div>
  )
}
